import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { ImagePlus, Trash2, UploadCloud, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const ACCEPT = ".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml";
const ALLOWED_MIME = new Set(["image/png", "image/jpeg", "image/webp", "image/svg+xml"]);

/**
 * Brand logo uploader for investor reports (SREO, bundle PDFs, portfolio snapshots).
 * Backend: POST/DELETE /api/user/logo — stores as a data URI on the user doc.
 */
export default function BrandLogoSection() {
  const { refresh } = useAuth();
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/user/settings");
      setLogo(data.logo_data_uri || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-selecting same file re-fires
    if (!file) return;
    if (!ALLOWED_MIME.has(file.type)) {
      toast.error("Unsupported format. Use PNG, JPG, WEBP, or SVG.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File too large — ${(file.size / 1024 / 1024).toFixed(2)} MB. Max 2 MB.`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/user/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded — reports will now carry your brand");
      await load();
      // Refresh the auth context so the Dashboard / Reports header logo
      // updates immediately without a page reload.
      refresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove your brand logo from all future reports?")) return;
    setBusy(true);
    try {
      await api.delete("/user/logo");
      toast.success("Logo removed");
      setLogo(null);
      refresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-sm p-6 mb-6"
      data-testid="brand-logo-section"
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 bg-indigo-600 text-white flex items-center justify-center rounded-sm">
          <ImagePlus className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Brand logo for investor reports
          </h2>
          <p className="text-sm text-slate-500">
            Appears in the top-left of every PDF report you generate or email — SREO, rent roll,
            expense summary, portfolio snapshot, loan portfolio, monthly digest. PNG, JPG, WEBP, or SVG
            up to 2 MB. Transparent background recommended.
          </p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={onFile}
        className="hidden"
        data-testid="brand-logo-input"
      />

      <div className="grid md:grid-cols-[auto,1fr] gap-5 items-center">
        <div
          className="w-48 h-28 border border-dashed border-slate-300 rounded-sm flex items-center justify-center bg-slate-50 overflow-hidden"
          data-testid="brand-logo-preview"
        >
          {loading ? (
            <span className="text-xs text-slate-400 font-mono">Loading…</span>
          ) : logo ? (
            <img
              src={logo}
              alt="Current brand logo"
              className="max-w-full max-h-full object-contain"
              data-testid="brand-logo-image"
            />
          ) : (
            <div className="text-center text-xs text-slate-400">
              <UploadCloud className="w-6 h-6 mx-auto mb-1 text-slate-300" />
              No logo yet
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onPick}
              disabled={busy}
              data-testid="upload-brand-logo-btn"
              className="btn-primary-dark flex items-center gap-2 text-sm disabled:opacity-60"
            >
              <UploadCloud className="w-4 h-4" />
              {busy ? "Uploading…" : logo ? "Replace logo" : "Upload logo"}
            </button>
            {logo && (
              <button
                type="button"
                onClick={onRemove}
                disabled={busy}
                data-testid="remove-brand-logo-btn"
                className="btn-outline-dark flex items-center gap-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                <Trash2 className="w-4 h-4" /> Remove
              </button>
            )}
          </div>
          {logo ? (
            <div
              className="inline-flex items-center gap-2 text-xs text-emerald-700 font-semibold"
              data-testid="brand-logo-status"
            >
              <CheckCircle2 className="w-4 h-4" /> Your logo will appear on all generated reports.
            </div>
          ) : (
            <div className="text-xs text-slate-500">
              Without a logo, reports fall back to the "RealtyVault Manager" header.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
